/**
 * Classifies environment failure messages into structured error info.
 *
 * This is a frontend bridge: it pattern-matches on `failureMessage` strings from
 * environment status and returns a classified error with code, message, and metadata.
 * When the backend starts attaching structured errors to Connect responses,
 * this classifier becomes the fallback for environments without structured errors.
 *
 * Error codes and default user messages are sourced from the canonical registry
 * (backend/api/errorcodes/codes.go) via the generated file error-codes.generated.ts.
 *
 * Actions (UI buttons) are intentionally separate from the error classification.
 * The classifier determines *what* went wrong; the ERROR_ACTIONS mapping determines
 * *how* the frontend lets the user respond. This keeps the error shape backend-compatible
 * and the action mapping a pure frontend concern.
 */

import {
    AUTOMATIONS_FILE_NOT_FOUND,
    AUTOMATIONS_FILE_PARSE_ERROR,
    AUTOMATIONS_FILE_PERMISSION_DENIED,
    CONTENT_GIT_ACCESS_DENIED,
    CONTENT_GIT_AUTH_FAILED,
    CONTENT_GIT_HTTP_AUTH_DENIED,
    CONTENT_GIT_INVALID_TOKEN,
    CONTENT_GIT_REPO_NOT_FOUND,
    CONTENT_GIT_SSO_EXPIRED,
    CONTENT_INVALID_CLONE_TARGET,
    DEVCONTAINER_BUILD_FAILED,
    DEVCONTAINER_CREATE_FAILED,
    DEVCONTAINER_DNS_FAILED,
    DEVCONTAINER_DOCKERFILE_ERROR,
    DEVCONTAINER_RECOVERY_FAILED,
    DEVCONTAINER_REGISTRY_ERROR,
    MACHINE_AGENT_TIMEOUT,
    MACHINE_CONFIG_DELIVERY_FAILED,
    MACHINE_LOCAL_NETWORK,
    MACHINE_OUT_OF_CAPACITY,
    MACHINE_SNAPSHOT_FAILED,
    MACHINE_SSM_ACCESS_DENIED,
    MACHINE_SUBNET_EXHAUSTED,
    MACHINE_SYSTEM_EXCEPTION,
    MACHINE_UNEXPECTED_EXIT,
    MACHINE_VM_TERMINATED,
    SCM_RATE_LIMITED,
} from "@/utils/error-codes.generated";

export type ErrorActionTarget = { type: "dashboard_path"; path: string } | { type: "external_url"; url: string };

export type ErrorActionInfo = {
    actionId: string;
    label: string;
    target: ErrorActionTarget;
};

/** Pure error classification — no UI actions. */
export type ActionableError = {
    errorCode: string;
    userMessage: string;
    metadata: Record<string, string>;
};

/** Optional context passed to the classifier to enrich error messages. */
export type ClassifierContext = {
    /** The repository URL, shown in error messages so users know which repo failed. */
    repoUrl?: string;
};

type ErrorPattern = {
    /** Substring(s) to match against the failure message (case-insensitive). Any match triggers. */
    patterns: string[];
    /** The classified error to return when matched. */
    error: ActionableError;
};

const GIT_AUTH_SETTINGS_ACTION: ErrorActionInfo = {
    actionId: "configure_git_auth",
    label: "Configure Git Authentication",
    target: { type: "dashboard_path", path: "/settings/git-authentications" },
};

/**
 * Maps error codes to frontend actions. This is a UI concern — the backend
 * should not dictate what buttons to show. The classifier returns the error
 * classification; consumers look up actions here by error code.
 */
export const ERROR_ACTIONS: Record<string, ErrorActionInfo[]> = {
    [CONTENT_GIT_ACCESS_DENIED.code]: [GIT_AUTH_SETTINGS_ACTION],
    [CONTENT_GIT_AUTH_FAILED.code]: [GIT_AUTH_SETTINGS_ACTION],
    [CONTENT_GIT_HTTP_AUTH_DENIED.code]: [GIT_AUTH_SETTINGS_ACTION],
    [CONTENT_GIT_INVALID_TOKEN.code]: [GIT_AUTH_SETTINGS_ACTION],
    [SCM_RATE_LIMITED.code]: [],
};

/**
 * Content/git error patterns, ordered from most specific to least specific.
 * Order matters: the first match wins.
 */
const CONTENT_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["Cannot find valid SSO", "SSO session"],
        error: {
            errorCode: CONTENT_GIT_SSO_EXPIRED.code,
            // Override: more specific than the registry default — tells the user
            // to re-authenticate with their identity provider, not just "continue".
            userMessage: "Your SSO session has expired. Re-authenticate with your identity provider.",
            metadata: {},
        },
    },
    {
        patterns: ["HTTP Basic: Access denied"],
        error: {
            errorCode: CONTENT_GIT_HTTP_AUTH_DENIED.code,
            // Override: shorter, action-oriented phrasing for the dashboard context
            // where the user can immediately re-authenticate via the settings link.
            userMessage: "Git HTTP authentication denied. Check your credentials or re-authenticate.",
            metadata: {},
        },
    },
    {
        patterns: ["Repository not found"],
        error: {
            errorCode: CONTENT_GIT_REPO_NOT_FOUND.code,
            userMessage: CONTENT_GIT_REPO_NOT_FOUND.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["Invalid username"],
        error: {
            errorCode: CONTENT_GIT_INVALID_TOKEN.code,
            userMessage: CONTENT_GIT_INVALID_TOKEN.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["rate limit", "rate_limit"],
        error: { errorCode: SCM_RATE_LIMITED.code, userMessage: SCM_RATE_LIMITED.userMessage, metadata: {} },
    },
    {
        patterns: ["CLONE_TARGET_MODE_UNSPECIFIED"],
        error: {
            errorCode: CONTENT_INVALID_CLONE_TARGET.code,
            userMessage: CONTENT_INVALID_CLONE_TARGET.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["authentication failed", "not authenticated"],
        error: {
            errorCode: CONTENT_GIT_AUTH_FAILED.code,
            userMessage: CONTENT_GIT_AUTH_FAILED.userMessage,
            metadata: {},
        },
    },
    {
        // Most generic git access pattern — must be last among git auth errors
        patterns: ["access denied"],
        error: {
            errorCode: CONTENT_GIT_ACCESS_DENIED.code,
            userMessage: CONTENT_GIT_ACCESS_DENIED.userMessage,
            metadata: {},
        },
    },
];

/**
 * Machine/infrastructure error patterns, ordered from most specific to least specific.
 */
const MACHINE_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["ssm:SendCommand", "SSM"],
        error: {
            errorCode: MACHINE_SSM_ACCESS_DENIED.code,
            userMessage: MACHINE_SSM_ACCESS_DENIED.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["InsufficientFreeAddresses", "subnet"],
        error: {
            errorCode: MACHINE_SUBNET_EXHAUSTED.code,
            userMessage: MACHINE_SUBNET_EXHAUSTED.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["terminated externally"],
        error: { errorCode: MACHINE_VM_TERMINATED.code, userMessage: MACHINE_VM_TERMINATED.userMessage, metadata: {} },
    },
    {
        patterns: ["could not reach the environment", "Local Network permission"],
        error: { errorCode: MACHINE_LOCAL_NETWORK.code, userMessage: MACHINE_LOCAL_NETWORK.userMessage, metadata: {} },
    },
    {
        patterns: ["agent has been inactive", "agent inactive"],
        error: { errorCode: MACHINE_AGENT_TIMEOUT.code, userMessage: MACHINE_AGENT_TIMEOUT.userMessage, metadata: {} },
    },
    {
        patterns: ["could not be prepared for snapshotting"],
        error: {
            errorCode: MACHINE_SNAPSHOT_FAILED.code,
            userMessage: MACHINE_SNAPSHOT_FAILED.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["failed to send the initial configuration"],
        error: {
            errorCode: MACHINE_CONFIG_DELIVERY_FAILED.code,
            userMessage: MACHINE_CONFIG_DELIVERY_FAILED.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["out of capacity"],
        error: {
            errorCode: MACHINE_OUT_OF_CAPACITY.code,
            userMessage: MACHINE_OUT_OF_CAPACITY.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["exited unexpectedly"],
        error: {
            errorCode: MACHINE_UNEXPECTED_EXIT.code,
            userMessage: MACHINE_UNEXPECTED_EXIT.userMessage,
            metadata: {},
        },
    },
    {
        // Most generic machine pattern — must be last
        patterns: ["system exception"],
        error: {
            errorCode: MACHINE_SYSTEM_EXCEPTION.code,
            userMessage: MACHINE_SYSTEM_EXCEPTION.userMessage,
            metadata: {},
        },
    },
];

const AUTOMATIONS_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["not found", "no such file"],
        error: {
            errorCode: AUTOMATIONS_FILE_NOT_FOUND.code,
            userMessage: AUTOMATIONS_FILE_NOT_FOUND.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["syntax", "parse error", "unmarshal", "cannot read"],
        error: {
            errorCode: AUTOMATIONS_FILE_PARSE_ERROR.code,
            userMessage: AUTOMATIONS_FILE_PARSE_ERROR.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["permission denied", "403"],
        error: {
            errorCode: AUTOMATIONS_FILE_PERMISSION_DENIED.code,
            userMessage: AUTOMATIONS_FILE_PERMISSION_DENIED.userMessage,
            metadata: {},
        },
    },
];

/**
 * DevContainer error patterns, ordered from most specific to least specific.
 */
const DEVCONTAINER_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["Creating recovery container"],
        error: {
            errorCode: DEVCONTAINER_RECOVERY_FAILED.code,
            userMessage: DEVCONTAINER_RECOVERY_FAILED.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["DNS", "name resolution"],
        error: {
            errorCode: DEVCONTAINER_DNS_FAILED.code,
            userMessage: DEVCONTAINER_DNS_FAILED.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["pull access denied", "manifest unknown", "unauthorized"],
        error: {
            errorCode: DEVCONTAINER_REGISTRY_ERROR.code,
            userMessage: DEVCONTAINER_REGISTRY_ERROR.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["Dockerfile", "dockerfile parse"],
        error: {
            errorCode: DEVCONTAINER_DOCKERFILE_ERROR.code,
            userMessage: DEVCONTAINER_DOCKERFILE_ERROR.userMessage,
            metadata: {},
        },
    },
    {
        patterns: ["Building dev container"],
        error: {
            errorCode: DEVCONTAINER_BUILD_FAILED.code,
            userMessage: DEVCONTAINER_BUILD_FAILED.userMessage,
            metadata: {},
        },
    },
    {
        // Generic devcontainer creation failure — must be last
        patterns: ["Creating dev container", "cannot run devcontainer up"],
        error: {
            errorCode: DEVCONTAINER_CREATE_FAILED.code,
            userMessage: DEVCONTAINER_CREATE_FAILED.userMessage,
            metadata: {},
        },
    },
];

const PATTERNS_BY_COMPONENT: Record<string, ErrorPattern[]> = {
    content: CONTENT_ERROR_PATTERNS,
    machine: MACHINE_ERROR_PATTERNS,
    automations: AUTOMATIONS_ERROR_PATTERNS,
    devcontainer: DEVCONTAINER_ERROR_PATTERNS,
};

function matchPatterns(failureMessage: string, patterns: ErrorPattern[]): ActionableError | null {
    const lower = failureMessage.toLowerCase();
    for (const pattern of patterns) {
        if (pattern.patterns.some((p) => lower.includes(p.toLowerCase()))) {
            return pattern.error;
        }
    }
    return null;
}

/** Error codes where the repo URL is relevant context for the user message. */
const REPO_URL_ERROR_CODES = new Set<string>([
    CONTENT_GIT_ACCESS_DENIED.code,
    CONTENT_GIT_AUTH_FAILED.code,
    CONTENT_GIT_HTTP_AUTH_DENIED.code,
    CONTENT_GIT_INVALID_TOKEN.code,
    CONTENT_GIT_SSO_EXPIRED.code,
    CONTENT_GIT_REPO_NOT_FOUND.code,
]);

/**
 * Enriches a classified error with context (e.g. repo URL).
 * Returns a new object — the pattern templates are not mutated.
 */
function applyContext(error: ActionableError, context?: ClassifierContext): ActionableError {
    if (!context?.repoUrl || !REPO_URL_ERROR_CODES.has(error.errorCode)) {
        return error;
    }

    return { ...error, metadata: { ...error.metadata, repoUrl: context.repoUrl } };
}

/**
 * Classifies an environment failure message into a structured error.
 *
 * @param component - The environment status component that failed
 * @param failureMessage - The raw failure message string
 * @param context - Optional context (e.g. repo URL) to enrich the error
 * @returns Classified error if the message matches a known pattern, null otherwise
 */
export function classifyEnvironmentError(
    component: "content" | "machine" | "automations" | "devcontainer" | "secrets",
    failureMessage: string | undefined,
    context?: ClassifierContext,
): ActionableError | null {
    if (!failureMessage) {
        return null;
    }

    const patterns = PATTERNS_BY_COMPONENT[component];
    if (!patterns) {
        return null;
    }

    const match = matchPatterns(failureMessage, patterns);
    if (!match) {
        return null;
    }

    return applyContext(match, context);
}
